import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { TenantService } from '../services/tenant.service';

/**
 * ARC-066: em load a frio (nova aba/refresh, não navegação em sessão),
 * `TenantService.availableTenants()` ainda está vazio — o antigo guard
 * síncrono tratava isso como "sem acesso" e mandava para
 * `/selecionar-tenant` sem preservar o destino pedido. Agora: se a lista
 * ainda não carregou, carrega antes de decidir, e resolve o tenant pelo
 * segmento `:tenantId` da URL sem forçar re-seleção quando o usuário tem
 * acesso. Só redireciona para seleção quando genuinamente necessário —
 * e, nesse caso, preserva a URL pedida em `returnUrl` para retomar depois.
 */
export const tenantGuard: CanActivateFn = (route, state) => {
  const tenantService = inject(TenantService);
  const router = inject(Router);

  if (tenantService.mode() !== 'multitenant') {
    return router.createUrlTree(['/']);
  }

  const tenantId = route.paramMap.get('tenantId');
  if (!tenantId) {
    return router.createUrlTree(['/selecionar-tenant']);
  }

  const resolve = (): boolean | UrlTree => {
    if (tenantService.canUseTenant(tenantId)) {
      tenantService.activateFromRoute(tenantId);
      return true;
    }
    return router.createUrlTree(['/selecionar-tenant'], { queryParams: { returnUrl: state.url } });
  };

  if (tenantService.availableTenants().length > 0) {
    return resolve();
  }

  return tenantService.loadTenantsForUser().pipe(
    map(() => resolve()),
    catchError(() =>
      of(router.createUrlTree(['/selecionar-tenant'], { queryParams: { returnUrl: state.url } }))
    )
  ) satisfies Observable<boolean | UrlTree>;
};
